import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '../../../constants';
import { useTranslation } from '../../../contexts/TranslationContext';
import { gamesApi } from '../../../services/gamesService';
import { Game } from '../../../types/interfaces';
import GamePanel from '../GamePanel/GamePanel';
import './FeedGrid.scss';

const FEED_GAME_COUNT = 8;
type FeedTab = 'upcoming' | 'live';

const FeedGrid: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [upcomingGames, setUpcomingGames] = useState<Game[]>([]);
  const [liveGames, setLiveGames] = useState<Game[]>([]);
  const [activeTab, setActiveTab] = useState<FeedTab>('upcoming');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [upcoming, live] = await Promise.all([
          gamesApi.getFeed(FEED_GAME_COUNT),
          gamesApi.getFeed(FEED_GAME_COUNT, undefined, undefined, true),
        ]);
        if (!cancelled) {
          setUpcomingGames(upcoming);
          setLiveGames(live);
        }
      } catch {
        if (!cancelled) {
          setUpcomingGames([]);
          setLiveGames([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const isLive = activeTab === 'live';
  const games = isLive ? liveGames : upcomingGames;
  const featuredGame = games[0];
  const normalGames = games.slice(1);

  if (loading || upcomingGames.length === 0) {
    return null;
  }

  return (
    <div className="feed-grid">
      <div className="feed-grid__tabs">
        <button
          className={`feed-grid__tab${activeTab === 'upcoming' ? ' feed-grid__tab--active' : ''}`}
          onClick={() => setActiveTab('upcoming')}
        >
          {t.feed.upcomingPicks}
        </button>
        {liveGames.length > 0 && (
          <button
            className={`feed-grid__tab${activeTab === 'live' ? ' feed-grid__tab--active' : ''}`}
            onClick={() => setActiveTab('live')}
          >
            {t.feed.currentPicks}
          </button>
        )}
      </div>

      <div className="feed-grid__content">
        {featuredGame && (
          <div className="feed-grid__featured">
            <GamePanel
              game={featuredGame}
              featured
              isLive={isLive}
              onClick={() => navigate(ROUTES.EXPERTS_RECOMMENDATIONS_GAME(featuredGame.id), { state: { game: featuredGame } })}
            />
          </div>
        )}

        {normalGames.map(game => (
          <div key={game.id} className="feed-grid__cell">
            <GamePanel
              game={game}
              isLive={isLive}
              onClick={() => navigate(ROUTES.EXPERTS_RECOMMENDATIONS_GAME(game.id), { state: { game } })}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default FeedGrid;
